import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationInstrumentationDevicesModal
    from '@/modules/configurationModule/detail/instrumentation/devices/components/modal/configuration-instrumentation-devices-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    columnsDefInstrumentationDevicesGrid,
    ConfigurationInstrumentationDevicesGridState,
    initialConfigurationInstrumentationDevicesGridState
} from '@/modules/configurationModule/detail/instrumentation/devices/components/grid/configuration-instrumentation-devices-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationDeviceCreateRequest,
    ConfigurationDevicePatchInBatchRequest,
    ConfigurationDeviceResponse
} from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-device/configuration-device.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { CommonDeviceTypeEnumResponse } from '@/api/services/common/device-type/common-device-type.dto'
import { commonDeviceTypeService } from '@/api/services/common/device-type/common-device-type.service'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationInstrumentationDevicesModal
    }
})
export default class ConfigurationInstrumentationDevicesGrid extends BaseCrudServerSideComponent<ConfigurationDeviceResponse> implements BaseCrud {
    @Ref() readonly devicesModalWrapper!: typeof ModalWrapper

    state: ConfigurationInstrumentationDevicesGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationInstrumentationDevicesGridState()
    }

    /// SERVICES CALLS ///

    private async _getDeviceTypes(): Promise<CommonDeviceTypeEnumResponse[]> {
        const response = await this.$_toCallSecure(commonDeviceTypeService.getDeviceTypes())
        return response ?? []
    }

    private async _createDevice(createRequest: ConfigurationDeviceCreateRequest): Promise<ConfigurationDeviceResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchDevices(batchRequests: ConfigurationDevicePatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteDevices(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// HOOKS ///

    async created(): Promise<void> {
        const deviceTypes = await this._getDeviceTypes()
        this.state.columnsDef = columnsDefInstrumentationDevicesGrid(deviceTypes)
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, devices: ConfigurationDeviceResponse[]): Promise<void> {
        if (devices?.length) {
            const batchRequests = devices.filter(device => device.active !== active)
                .map(device => ({
                    id: device.id,
                    active
                }))
            if (await this._patchDevices(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationDeviceCreateRequest): Promise<void> {
        if (createRequest) {
            const device = await this._createDevice(createRequest)
            if (device) {
                this.reloadData()
                this.onUpdate(device)
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationDevicePatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchDevices([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteDevices(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(device: ConfigurationDeviceResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (device) {
            this.showModal(this.$tc('configuration.actions.update'), device, this.devicesModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(device => device.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.devicesModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }
}
